import { observer } from 'mobx-react';
import React from 'react';

import { GridLegacy as Grid } from '@mui/material';

import { Form, Formik, FormikHelpers } from 'formik';

import dayjs, { Dayjs } from 'dayjs';

import { AxiosError } from 'axios';

import { Button } from 'components/Button';
import NavigationBlocker from 'components/NavigationBlocker';

import { StyledTextField, StyledSelect, StyledDatePicker } from 'components/Input/styles';

import CustomUserDropdownField from 'components/SharedFormComponents/CustomUserDropdownField';
import EntitySearchField from 'components/EntitySearchField';
import CountrySelect from 'components/CountrySelect';

import API from 'utils/api';

import { EntityModules, UserRole } from 'core/constants';

import { UpdatedUserPayload } from 'utils/events/types';
import { postMessage } from 'utils/events/broadcast';
import { EventType } from 'utils/events/constants';

import { UuidableName } from 'utils/api/types';
import {
  UpdateUserPayload,
  UserAttributesUpdate,
  UserFieldOption,
  UserFieldDropdownKind,
} from 'utils/api/modules/user/user.types';
import { getErrorMessage, isBadRequest } from 'utils/api/errors';

import Logger from 'utils/logger';

import { useStore } from 'utils/hooks/store';

import useMappedFields from '../../hooks/useMappedFields';
import { IDP_FIELDS } from '../IdpManaged';
import { StyledSectionGrid, StyledRowTitle, ButtonWrapper } from '../EditPersonalInformation/styles';
import { StyledMenuItem } from './styles';
import { UserJobInformationFormValues, UserJobInformationSchema, mapJobInformationErrors } from './types';

const EditRow: React.FC<{ name: string; children: React.ReactNode; id?: string }> = ({ name, children, id }) => (
  <>
    <StyledSectionGrid item container xs={12} alignContent="flex-start" id={id} marginTop="16px">
      <StyledRowTitle $disabled={false}>{name}</StyledRowTitle>
    </StyledSectionGrid>
    <StyledSectionGrid item container xs={12}>
      {children}
    </StyledSectionGrid>
  </>
);

const withCurrent = (options: UserFieldOption[], current: UuidableName | null | undefined): UserFieldOption[] => {
  if (!current || options.some((option) => option.uuid === current.uuid)) {
    return options;
  }
  return [{ uuid: current.uuid, name: current.name, isActive: false, ordering: -1 }, ...options];
};

const UserJobInformationEdit: React.FunctionComponent<{ setEditing: (val: boolean) => void }> = ({ setEditing }) => {
  const {
    userDetails: { details },
    notification,
    session,
  } = useStore();

  const canEditManager = session.user?.role !== UserRole.User;
  const showObjectManager = !!details?.hasObjectManager;
  const showSite = (session.user?.organisation?.maximumSitesAllowed ?? 0) > 0;

  const [departmentOptions, setDepartmentOptions] = React.useState<UserFieldOption[]>([]);
  const [regionOptions, setRegionOptions] = React.useState<UserFieldOption[]>([]);
  const [employmentTypeOptions, setEmploymentTypeOptions] = React.useState<UserFieldOption[]>([]);
  const [optionsLoading, setOptionsLoading] = React.useState(true);
  const [startDateOpen, setStartDateOpen] = React.useState(false);
  const { isMapped } = useMappedFields();

  React.useEffect(() => {
    let active = true;
    const kinds: [UserFieldDropdownKind, (options: UserFieldOption[]) => void][] = [
      ['department', setDepartmentOptions],
      ['employment-type', setEmploymentTypeOptions],
    ];
    if (showObjectManager) {
      kinds.push(['region', setRegionOptions]);
    }
    Promise.all(kinds.map(([kind]) => API.user.listUserFieldOptions(kind)()))
      .then((responses) => {
        if (!active) {
          return;
        }
        responses.forEach((response, index) => kinds[index][1](response.data));
      })
      .catch(() => {})
      .finally(() => {
        if (active) {
          setOptionsLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [showObjectManager]);

  if (!details) {
    return null;
  }

  const { attributes } = details;

  const initialValues: UserJobInformationFormValues = {
    jobTitle: attributes?.jobTitle ?? '',
    department: attributes?.department?.uuid ?? '',
    manager: details.manager ? { uuid: details.manager.uuid, name: details.manager.name } : null,
    employmentType: attributes?.employmentType?.uuid ?? '',
    costCentre: details.personalDetails.costCentre ?? '',
    site: attributes?.site ? { uuid: attributes.site.uuid, name: attributes.site.name } : null,
    region: attributes?.region?.uuid ?? '',
    country: attributes?.country ?? '',
    startDate: attributes?.startDate ?? null,
  };

  const onUpdate = async (
    data: UserJobInformationFormValues,
    helpers: FormikHelpers<UserJobInformationFormValues>
  ): Promise<void> => {
    try {
      helpers.setSubmitting(true);

      const attributesPayload: UserAttributesUpdate = {};
      if (data.jobTitle !== initialValues.jobTitle) {
        attributesPayload.jobTitle = data.jobTitle;
      }
      if (data.country !== initialValues.country) {
        attributesPayload.country = data.country;
      }
      if (data.department !== initialValues.department) {
        attributesPayload.department = data.department || null;
      }
      if (data.region !== initialValues.region) {
        attributesPayload.region = data.region || null;
      }
      if (data.employmentType !== initialValues.employmentType) {
        attributesPayload.employmentType = data.employmentType || null;
      }
      if ((data.site?.uuid ?? null) !== (initialValues.site?.uuid ?? null)) {
        attributesPayload.site = data.site?.uuid ?? null;
      }
      if (data.startDate !== initialValues.startDate) {
        attributesPayload.startDate = data.startDate;
      }
      if (data.costCentre !== initialValues.costCentre) {
        attributesPayload.costCentre = data.costCentre || null;
      }

      const payload: UpdateUserPayload = {
        attributes: attributesPayload,
      };
      if (canEditManager && (data.manager?.uuid ?? null) !== (initialValues.manager?.uuid ?? null)) {
        payload.manager = data.manager?.uuid ?? null;
      }

      const response = await API.user.updateUserData(details.uuid, payload)();
      const updated: UpdatedUserPayload = response.data;
      postMessage(EventType.UpdatedUser, updated);
      notification.enqueueSuccessSnackbar('User details updated successfully!');

      helpers.setStatus();
      helpers.setErrors({});
      setEditing(false);
    } catch (e) {
      const error = e as AxiosError;
      notification.enqueueErrorSnackbar(getErrorMessage(error, 'Could not update user details'));
      if (isBadRequest(error)) {
        const fieldErrors = mapJobInformationErrors(error.response?.data as Record<string, unknown> | undefined);
        helpers.setErrors(fieldErrors);
      } else {
        Logger.error('Invalid update user attributes API response', e);
      }
    } finally {
      helpers.setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, helpers) => onUpdate(values, helpers)}
      validationSchema={UserJobInformationSchema}
    >
      {({ values, handleChange, touched, errors, isSubmitting, setFieldValue, dirty }) => (
        <Form>
          <NavigationBlocker
            when={dirty}
            shouldBlockNavigation={() => dirty}
            navigateMessage="You have unsaved changes. Do you want to discard them or stay on this page?"
          />
          <Grid container sx={{ padding: '20px' }}>
            <EditRow name="Job title">
              <StyledTextField
                fullWidth
                id="jobTitle"
                name="jobTitle"
                value={values.jobTitle}
                helperText={touched.jobTitle ? errors.jobTitle : ''}
                error={(touched.jobTitle || false) && errors.jobTitle !== undefined}
                disabled={isSubmitting || isMapped(IDP_FIELDS.jobTitle)}
                onChange={handleChange}
              />
            </EditRow>

            <EditRow name="Department">
              <StyledSelect
                fullWidth
                displayEmpty
                disabled={isSubmitting || optionsLoading || isMapped(IDP_FIELDS.department)}
                error={errors.department !== undefined}
                value={values.department}
                onChange={(event) => setFieldValue('department', event.target.value as string)}
              >
                <StyledMenuItem value="">Not set</StyledMenuItem>
                {withCurrent(departmentOptions, attributes?.department).map((option) => (
                  <StyledMenuItem key={option.uuid} value={option.uuid}>
                    {option.name}
                  </StyledMenuItem>
                ))}
              </StyledSelect>
            </EditRow>

            <EditRow name="Line manager">
              {canEditManager && !isMapped(IDP_FIELDS.manager) ? (
                <CustomUserDropdownField
                  description="Line manager"
                  helperText={touched.manager ? (errors.manager as string) : ''}
                  error={(touched.manager || false) && errors.manager !== undefined}
                  value={values.manager}
                  onChange={(value) => setFieldValue('manager', value ? { uuid: value.uuid, name: value.name } : null)}
                  disableInitialLoad
                />
              ) : (
                <StyledTextField fullWidth value={values.manager?.name ?? ''} disabled />
              )}
            </EditRow>

            <EditRow name="Employment type">
              <StyledSelect
                fullWidth
                displayEmpty
                disabled={isSubmitting || optionsLoading || isMapped(IDP_FIELDS.employmentType)}
                error={errors.employmentType !== undefined}
                value={values.employmentType}
                onChange={(event) => setFieldValue('employmentType', event.target.value as string)}
              >
                <StyledMenuItem value="">Not set</StyledMenuItem>
                {withCurrent(employmentTypeOptions, attributes?.employmentType).map((option) => (
                  <StyledMenuItem key={option.uuid} value={option.uuid}>
                    {option.name}
                  </StyledMenuItem>
                ))}
              </StyledSelect>
            </EditRow>

            <EditRow name="Start date">
              <StyledDatePicker
                sx={{ width: '100%' }}
                defaultValue={values.startDate ? dayjs(values.startDate) : null}
                disabled={isSubmitting || isMapped(IDP_FIELDS.startDate)}
                open={startDateOpen}
                onClose={() => setStartDateOpen(false)}
                onChange={(date: Dayjs | null) => {
                  if (date === null) {
                    setFieldValue('startDate', null);
                  } else if (date.isValid()) {
                    setFieldValue('startDate', date.format('YYYY-MM-DD'));
                  }
                }}
                slotProps={{
                  field: { clearable: true },
                  textField: {
                    onClick: () => {
                      if (!isSubmitting) {
                        setStartDateOpen((prev) => !prev);
                      }
                    },
                    error: errors.startDate !== undefined,
                    helperText: touched.startDate ? errors.startDate : '',
                  },
                }}
                format="DD/MM/YYYY"
              />
            </EditRow>

            <EditRow name="Cost centre">
              <StyledTextField
                fullWidth
                id="costCentre"
                name="costCentre"
                value={values.costCentre}
                helperText={touched.costCentre ? errors.costCentre : ''}
                error={(touched.costCentre || false) && errors.costCentre !== undefined}
                disabled={isSubmitting || isMapped(IDP_FIELDS.costCentre)}
                onChange={handleChange}
              />
            </EditRow>

            {showSite && (
              <EditRow name="Site">
                {/* EntitySearchField has no disabled state, so a mapped site falls back to the same
                    read-only text field a non-editable line manager already uses. */}
                {isMapped(IDP_FIELDS.site) ? (
                  <StyledTextField fullWidth value={values.site?.name ?? ''} disabled />
                ) : (
                  <EntitySearchField
                    field="site"
                    module={EntityModules.Sites}
                    data={values.site}
                    error={errors.site !== undefined}
                    updateField={(_, value) => setFieldValue('site', value as UuidableName | null)}
                  />
                )}
              </EditRow>
            )}

            {showObjectManager && (
              <EditRow name="Region">
                <StyledSelect
                  fullWidth
                  displayEmpty
                  disabled={isSubmitting || optionsLoading || isMapped(IDP_FIELDS.region)}
                  error={errors.region !== undefined}
                  value={values.region}
                  onChange={(event) => setFieldValue('region', event.target.value as string)}
                >
                  <StyledMenuItem value="">Not set</StyledMenuItem>
                  {withCurrent(regionOptions, attributes?.region).map((option) => (
                    <StyledMenuItem key={option.uuid} value={option.uuid}>
                      {option.name}
                    </StyledMenuItem>
                  ))}
                </StyledSelect>
              </EditRow>
            )}

            <EditRow name="Country">
              <CountrySelect
                value={values.country}
                error={errors.country !== undefined}
                disabled={isSubmitting || isMapped(IDP_FIELDS.country)}
                onChange={(code) => setFieldValue('country', code)}
              />
            </EditRow>

            <ButtonWrapper>
              <Button variant="outlined" onClick={() => setEditing(false)} size="large">
                Cancel
              </Button>
              <Button variant="contained" type="submit" size="large" disabled={isSubmitting}>
                Save
              </Button>
            </ButtonWrapper>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default observer(UserJobInformationEdit);
